import * as React from 'react';
import { tz } from 'moment-timezone';
import { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { TimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { DropDownListComponent, MultiSelectComponent, CheckBoxSelection } from '@syncfusion/ej2-react-dropdowns';
import { UploaderComponent } from '@syncfusion/ej2-react-inputs';
import { ToolbarComponent, ItemsDirective, ItemDirective, ContextMenuComponent, AppBarComponent } from '@syncfusion/ej2-react-navigations';
import { ScheduleComponent, Day, Week, WorkWeek, Month, Year, TimelineViews, TimelineMonth, TimelineYear, ViewsDirective, ViewDirective, ResourcesDirective, ResourceDirective, Inject, Resize, DragAndDrop, Agenda, Print, ExcelExport, ICalendarImport, ICalendarExport, Timezone } from '@syncfusion/ej2-react-schedule';
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons';
import { addClass, Browser, closest, extend, Internationalization, isNullOrUndefined, removeClass, remove, compile } from '@syncfusion/ej2-base';
import { DataManager, Predicate, Query } from '@syncfusion/ej2-data';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from "../../auth/AuthenticatedAxios";
import './calander.css';
import { CalendarAssistantProvider, CalendarAssistantPanel, useCalendarAssistant } from '../CalendarAssistant';
import RightToolbar from '../RightToolbar/RightToolbar';

// Inner component that uses the calendar assistant context
const CalanderContent = () => {
    const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
    const authenticatedAxios = useAuthenticatedAxios();
    const [currentView, setCurrentView] = useState('Week');
    
    // Calendar Assistant context
    const { 
        isPanelOpen: isAssistantOpen, 
        togglePanel: onToggleAssistant, 
        isPinned: isPanelPinned,
        hasNewMessages,
        updateCurrentView
    } = useCalendarAssistant();
    const [isTimelineView, setIsTimelineView] = useState(false);
    let scheduleObj = useRef(null);
    let contextMenuObj = useRef(null);
    let timeBtn = useRef(null);
    let selectedTarget;
    let intl = new Internationalization();
    let workWeekObj = useRef(null);
    let resourceObj = useRef(null);
    let liveTimeInterval;
    const [calanderData, setCalanderData] = useState([]);
    const [calanderSettings, setCalanderSettings] = useState({});
    const [userCalanderSettings, setUserCalanderSettings] = useState({});
    const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState(null);

    useEffect(() => {
        let updatedView = currentView;
        switch (currentView) {
            case 'Day':
            case 'TimelineDay':
                updatedView = isTimelineView ? 'TimelineDay' : 'Day';
                break;
            case 'Week':
            case 'TimelineWeek':
                updatedView = isTimelineView ? 'TimelineWeek' : 'Week';
                break;
            case 'WorkWeek':
            case 'TimelineWorkWeek':
                updatedView = isTimelineView ? 'TimelineWorkWeek' : 'WorkWeek';
                break;
            case 'Month':
            case 'TimelineMonth':
                updatedView = isTimelineView ? 'TimelineMonth' : 'Month';
                break;
            case 'Year':
            case 'TimelineYear':
                updatedView = isTimelineView ? 'TimelineYear' : 'Year';
                break;
            case 'Agenda':
                updatedView = 'Agenda';
                break;
        }

        scheduleObj.current.currentView = updatedView;
        const source = axios.CancelToken.source();
        const fetchData = async () => {
            try {
                const token = await getAccessTokenSilently();
                const calanderDataReq = axios.get(`${process.env.REACT_APP_SERVER}calander/GetCalanderData`, {
                    cancelToken: source.token, headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                const calanderSettingsReq = axios.get(`${process.env.REACT_APP_SERVER}calander/CalanderSettings`, {
                    cancelToken: source.token, headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                const userCalanderSettingsReq = axios.get(`${process.env.REACT_APP_SERVER}calander/UserCalanderSettings`, {
                    cancelToken: source.token, headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                const [calanderDataRes, calanderSettingsRes, userCalanderSettingsRes] =
                    await axios.all([calanderDataReq, calanderSettingsReq, userCalanderSettingsReq]);

                let allEvents = calanderDataRes.data;

                // Also fetch Google Calendar events if connected
                try {
                    // Calculate date range (3 months back, 3 months forward)
                    const startDate = new Date();
                    startDate.setMonth(startDate.getMonth() - 3);
                    const endDate = new Date();
                    endDate.setMonth(endDate.getMonth() + 3);

                    const googleEventsRes = await axios.get(`${process.env.REACT_APP_SERVER}google-calendar/events`, {
                        cancelToken: source.token,
                        headers: { Authorization: `Bearer ${token}` },
                        params: {
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString()
                        }
                    });

                    if (googleEventsRes.data.googleCalendarEnabled && googleEventsRes.data.events) {
                        // Filter out Google events that are already synced from iMaven (have matching GoogleEventId)
                        const localGoogleEventIds = calanderDataRes.data
                            .filter(e => e.GoogleEventId)
                            .map(e => e.GoogleEventId);
                        
                        const uniqueGoogleEvents = googleEventsRes.data.events.filter(
                            ge => !localGoogleEventIds.includes(ge.GoogleEventId)
                        );
                        
                        console.log(`[Calendar] Local events count: ${calanderDataRes.data.length}`);
                        console.log(`[Calendar] Local events with GoogleEventId: ${localGoogleEventIds.length}`);
                        console.log(`[Calendar] Google events from API: ${googleEventsRes.data.events.length}`);
                        console.log(`[Calendar] Unique Google events after filter: ${uniqueGoogleEvents.length}`);
                        console.log(`[Calendar] Sample Google event:`, googleEventsRes.data.events[0]);
                        
                        allEvents = [...allEvents, ...uniqueGoogleEvents];
                        console.log(`[Calendar] Total events after merge: ${allEvents.length}`);
                        setGoogleCalendarConnected(true);
                    }
                } catch (googleError) {
                    console.log('Could not fetch Google Calendar events:', googleError.message);
                }

                setCalanderData(allEvents);
                setCalanderSettings(calanderSettingsRes.data);
                setUserCalanderSettings(userCalanderSettingsRes.data);

                initCalanderData(userCalanderSettingsRes.data);

            } catch (error) {
                console.log(error)
            }
        };
        fetchData();
        
        // Check Google Calendar connection status
        const checkGoogleCalendarStatus = async () => {
            try {
                const token = await getAccessTokenSilently();
                const response = await axios.get(`${process.env.REACT_APP_SERVER}google-calendar/status`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setGoogleCalendarConnected(response.data.connected && response.data.selectedCalendarId);
            } catch (error) {
                console.log('Google Calendar not connected or error checking status');
                setGoogleCalendarConnected(false);
            }
        };
        checkGoogleCalendarStatus();
        
        return () => {
            clearInterval(liveTimeInterval);
        };
    }, [isTimelineView]);

    const importTemplateFn = (data) => {
        const template = '<div class="e-template-btn"><span class="e-btn-icon e-icons e-upload-1 e-icon-left"></span>${text}</div>';
        return compile(template.trim())(data);
    };

    const updateLiveTime = () => {
        let scheduleTimezone = scheduleObj ? scheduleObj.current.timezone : 'Etc/GMT';
        let liveTime;
        if (scheduleObj.current.isAdaptive) {
            liveTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: scheduleTimezone });
        }
        else {
            liveTime = new Date().toLocaleTimeString('en-US', { timeZone: scheduleTimezone });
        }
        timeBtn.current.innerHTML = liveTime;
    };

    const onImportClick = (args) => {
        scheduleObj.current.importICalendar(args.event.target.files[0]);
    };

    const onPrint = () => {
        scheduleObj.current.print();
    };

    const onExportClick = (args) => {
        if (args.item.text === 'Excel') {
            let exportDatas = [];
            let eventCollection = scheduleObj.current.getEvents();
            let resourceCollection = scheduleObj.current.getResourceCollections();
            let resourceData = resourceCollection[0].dataSource;
            for (let resource of resourceData) {
                let data = eventCollection.filter((e) => e.CalendarId === resource.CalendarId);
                exportDatas = exportDatas.concat(data);
            }
            scheduleObj.current.exportToExcel({ exportType: 'xlsx', customData: exportDatas, fields: ['Id', 'Subject', 'StartTime', 'EndTime', 'CalendarId'] });
        }
        else {
            scheduleObj.current.exportToICalendar();
        }
    };

    const getEventData = () => {
        const date = scheduleObj.current.selectedDate;
        return {
            Id: scheduleObj.current.getEventMaxID(),
            Subject: '',
            StartTime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), new Date().getHours(), 0, 0),
            EndTime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), new Date().getHours() + 1, 0, 0),
            Location: '',
            Description: '',
            IsAllDay: false,
            CalendarId: 1
        };
    };

    const onToolbarItemClicked = (args) => {
        switch (args.item.text) {
            case 'Day':
                setCurrentView(isTimelineView ? 'TimelineDay' : 'Day');
                break;
            case 'Week':
                setCurrentView(isTimelineView ? 'TimelineWeek' : 'Week');
                break;
            case 'WorkWeek':
                setCurrentView(isTimelineView ? 'TimelineWorkWeek' : 'WorkWeek');
                break;
            case 'Month':
                setCurrentView(isTimelineView ? 'TimelineMonth' : 'Month');
                break;
            case 'Year':
                setCurrentView(isTimelineView ? 'TimelineYear' : 'Year');
                break;
            case 'Agenda':
                setCurrentView('Agenda');
                break;
            case 'New Event':
                const eventData = getEventData();
                scheduleObj.current.openEditor(eventData, 'Add', true);
                break;
            case 'New Recurring Event':
                const recEventData = getEventData();
                scheduleObj.current.openEditor(recEventData, 'Add', true, 1);
                break;
        }
    };

    const onChange = (args) => {
        setIsTimelineView(args.checked);
    };

    const timelineTemplate = useCallback(() => {
        return (<div className='template'>
            <div className='icon-child'>
                <CheckBoxComponent id='timeline_views' checked={isTimelineView} change={onChange} />
            </div>
            <div className='text-child'>Timeline Views</div>
        </div>);
    }, []);

    const groupTemplate = useCallback(() => {
        return (<div className='template'>
            <div className='icon-child'>
                <CheckBoxComponent id='grouping' checked={false} change={(args) => { scheduleObj.current.group.resources = args.checked ? ['Calendars'] : []; }} />
            </div>
            <div className='text-child'>Grouping</div>
        </div>);
    }, []);

    const gridlineTemplate = useCallback(() => {
        return (<div className='template'>
            <div className='icon-child'>
                <CheckBoxComponent id='timeSlots' checked={true} change={(args) => { scheduleObj.current.timeScale.enable = args.checked; }} />
            </div>
            <div className='text-child'>Gridlines</div>
        </div>);
    }, []);

    const autoHeightTemplate = useCallback(() => {
        return (<div className='template'>
            <div className='icon-child'>
                <CheckBoxComponent id='row_auto_height' checked={false} change={(args) => { scheduleObj.current.rowAutoHeight = args.checked; }} />
            </div>
            <div className='text-child'>Row Auto Height</div>
        </div>);
    }, []);

    const getDateHeaderDay = (value) => {
        return intl.formatDate(value, { skeleton: 'E' });
    };

    const getDateHeaderDate = (value) => {
        return intl.formatDate(value, { skeleton: 'd' });
    };

    const dateHeaderTemplate = (props) => {
        return (<Fragment>
            <div>{getDateHeaderDay(props.date)}</div>
            <div>{getDateHeaderDate(props.date)}</div>
        </Fragment>);
    };

    const onResourceChange = (args) => {
        let resourcePredicate;
        for (let value of args.value) {
            if (resourcePredicate) {
                resourcePredicate = resourcePredicate.or(new Predicate('CalendarId', 'equal', value));
            }
            else {
                resourcePredicate = new Predicate('CalendarId', 'equal', value);
            }
        }
        scheduleObj.current.resources[0].query = resourcePredicate ? new Query().where(resourcePredicate) : new Query().where('CalendarId', 'equal', 1);
    };

    const createUpload = () => {
        const element = document.querySelector('.calendar-import .e-css.e-btn');
        element.classList.add('e-inherit');
    };

    const btnClick = () => {
        let settingsPanel = document.querySelector('.overview-content .right-panel');
        if (settingsPanel.classList.contains('hide')) {
            removeClass([settingsPanel], 'hide');
            workWeekObj.current.refresh();
            resourceObj.current.refresh();
        }
        else {
            addClass([settingsPanel], 'hide');
        }
        scheduleObj.current.refreshEvents();
    };

    const contextMenuOpen = (args) => {
        let newEventElement = document.querySelector('.e-new-event');
        if (newEventElement) {
            remove(newEventElement);
            removeClass([document.querySelector('.e-selected-cell')], 'e-selected-cell');
        }
        scheduleObj.current.closeQuickInfoPopup();
        let targetElement = args.event.target;
        if (closest(targetElement, '.e-contextmenu')) {
            return;
        }
        selectedTarget = closest(targetElement, '.e-appointment,.e-work-cells,.e-vertical-view .e-date-header-wrap .e-all-day-cells,.e-vertical-view .e-date-header-wrap .e-header-cells');
        if (isNullOrUndefined(selectedTarget)) {
            args.cancel = true;
            return;
        }
        if (selectedTarget.classList.contains('e-appointment')) {
            const eventObj = scheduleObj.current.getEventDetails(selectedTarget);
            if (eventObj.RecurrenceRule) {
                // Recurring event: show delete options
                contextMenuObj.current.showItems(['Delete', 'DeleteOccurrence', 'DeleteSeries'], true);
                contextMenuObj.current.hideItems(['Add', 'AddRecurrence', 'Today', 'Save', 'EditRecurrenceEvent', 'EditOccurrence', 'EditSeries'], true);
            } else {
                // Single event: cancel context menu
                args.cancel = true;
                return;
            }
            return;
        }
        else if ((selectedTarget.classList.contains('e-work-cells') || selectedTarget.classList.contains('e-all-day-cells')) &&
            !selectedTarget.classList.contains('e-selected-cell')) {
            removeClass([].slice.call(scheduleObj.current.element.querySelectorAll('.e-selected-cell')), 'e-selected-cell');
            selectedTarget.setAttribute('aria-selected', 'true');
            selectedTarget.classList.add('e-selected-cell');
        }
        contextMenuObj.current.hideItems(['Save', 'Delete', 'EditRecurrenceEvent', 'DeleteRecurrenceEvent', 'EditOccurrence', 'EditSeries'], true);
        contextMenuObj.current.showItems(['Add', 'AddRecurrence', 'Today'], true);
    };

    const contextMenuSelect = (args) => {
        let selectedMenuItem = args.item.id;
        let eventObj = {};
        if (selectedTarget && selectedTarget.classList.contains('e-appointment')) {
            eventObj = scheduleObj.current.getEventDetails(selectedTarget);
        }
        switch (selectedMenuItem) {
            case 'Today':
                scheduleObj.current.selectedDate = new Date();
                break;
            case 'Add':
            case 'AddRecurrence':
                let selectedCells = scheduleObj.current.getSelectedElements();
                let activeCellsData = scheduleObj.current.getCellDetails(selectedCells.length > 0 ? selectedCells : selectedTarget);
                if (selectedMenuItem === 'Add') {
                    scheduleObj.current.openEditor(activeCellsData, 'Add');
                }
                else {
                    scheduleObj.current.openEditor(activeCellsData, 'Add', false, 1);
                }
                break;
            case 'Delete':
                scheduleObj.current.deleteEvent(eventObj);
                break;
            case 'DeleteOccurrence':
            case 'DeleteSeries':
                scheduleObj.current.deleteEvent(eventObj, selectedMenuItem);
                break;
            default:
                break;
        }
    };

    const timezoneChange = (args) => {
        scheduleObj.current.timezone = args.value;
        updateLiveTime();
        document.querySelector('.schedule-overview #timezoneBtn').innerHTML = args.itemData.text;
        if (userCalanderSettings.Timezone != args.value) {
            userCalanderSettings.Timezone = args.value
            updateCalanderSettings();
        }
    };

    const weekNumberChange = (args) => {
        if (args.value == "Off") {
            scheduleObj.current.showWeekNumber = false;
        }
        else {
            scheduleObj.current.showWeekNumber = true;
            scheduleObj.current.weekRule = args.value;
        }

        if (userCalanderSettings.WeekNumbers != args.value) {
            userCalanderSettings.WeekNumbers = args.value
            updateCalanderSettings();
        }
    };

    const tooltipChange = (args) => {
        if (args.value === "Off") {
            scheduleObj.current.eventSettings.enableTooltip = false;
        }
        else {
            scheduleObj.current.eventSettings.enableTooltip = true;
        }

        if (userCalanderSettings.Tooltip != args.value) {
            userCalanderSettings.Tooltip = args.value;
            updateCalanderSettings();
        }
    };

    const onActionComplete = async (args) => {
        console.log(args)
        const token = await getAccessTokenSilently();

        if (args.requestType == "eventCreated") {

            const newEvent = args.addedRecords[0]

            axios.post(`${process.env.REACT_APP_SERVER}calander/AddNewEvent`, { ...newEvent }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
                .then(response => {
                    console.log(response)
                })
                .catch(error => {
                    console.log(error)
                });

        } else if (args.requestType == "eventRemoved") {
            const event = args.deletedRecords && args.deletedRecords[0];
            if (event && event.Id) {
                authenticatedAxios.delete(`calander/DeleteEvent/${event.Id}`).then(res => {
                    console.log(res)
                }).catch(err => {
                    console.log(err);
                });
            } else {
                // Optionally log or handle the case where event or event.Id is missing
                console.warn("No valid event to delete:", event);
            }
        } else if (args.requestType == "eventChanged") {
            const eventToUpdate = args.changedRecords[0];
            axios.put(`${process.env.REACT_APP_SERVER}calander/UpdateEvent`, {
                ...eventToUpdate, headers: {
                    Authorization: `Bearer ${token}`
                }
            })
                .then(response => {
                    console.log(response)
                })
                .catch(error => {
                    console.log(error)
                });
        }
    }

    const onWorkWeekChange = (args) => {
        if (userCalanderSettings.WorkWeek != args.value) {
            scheduleObj.current.workDays = args.value
            userCalanderSettings.WorkWeek = args.value
            updateCalanderSettings();
        }
    }

    const onWorkDayChange = (args) => {
        if (userCalanderSettings.FirstDayOfTheWeek != args.value) {
            scheduleObj.current.firstDayOfWeek = args.value
            userCalanderSettings.FirstDayOfTheWeek = args.value
            updateCalanderSettings();
        }
    }

    const onDayStartHourChange = (args) => {
        try {
            const time = intl.formatDate(args.value, { skeleton: 'Hm' })
            if (userCalanderSettings.DayStartHour != time) {
                scheduleObj.current.startHour = time
                userCalanderSettings.DayStartHour = scheduleObj.current.startHour
                updateCalanderSettings();
            }
        } catch (error) {

        }
    }

    const onDayEndHourChange = (args) => {
        try {
            const time = intl.formatDate(args.value, { skeleton: 'Hm' })
            if (userCalanderSettings.DayEndHour != time) {
                scheduleObj.current.endHour = time
                userCalanderSettings.DayEndHour = scheduleObj.current.endHour
                updateCalanderSettings();
            }
        } catch (error) {

        }

    }

    const onWorkStartChange = (args) => {
        try {
            const time = intl.formatDate(args.value, { skeleton: 'Hm' })
            if (userCalanderSettings.WorkStartHour != time) {
                scheduleObj.current.workHours.start = time
                userCalanderSettings.WorkStartHour = scheduleObj.current.workHours.start
                updateCalanderSettings();
            }
        } catch (error) {
            console.log(error)
        }

    }

    const onWorkEndChange = (args) => {
        try {
            const time = intl.formatDate(args.value, { skeleton: 'Hm' })
            if (userCalanderSettings.WorkEndHour != time) {
                scheduleObj.current.workHours.end = time
                userCalanderSettings.WorkEndHour = scheduleObj.current.workHours.end;
                updateCalanderSettings();
            }
        } catch (error) {
            console.log(error)
        }

    }

    const onTimeSlotDurationChange = (args) => {
        if (userCalanderSettings.SlotDuration != args.value) {
            scheduleObj.current.timeScale.interval = args.value;
            userCalanderSettings.SlotDuration = scheduleObj.current.timeScale.interval;
            updateCalanderSettings();
        }
    }

    const onTimeSlotIntervalChange = (args) => {
        if (userCalanderSettings.SlotInterval != args.value) {
            scheduleObj.current.timeScale.slotCount = args.value;
            userCalanderSettings.SlotInterval = scheduleObj.current.timeScale.slotCount;
            updateCalanderSettings();
        }
    }

    const onTimeFormatChange = (args) => {
        if (args.value != userCalanderSettings.TimeFormat) {
            scheduleObj.current.timeFormat = args.value;
            userCalanderSettings.TimeFormat = scheduleObj.current.timeFormat;
            updateCalanderSettings();
        }
    }

    const updateCalanderSettings = async () => {
        console.log(userCalanderSettings)
        authenticatedAxios.put(`calander/UserCalanderSettings`, {
            ...userCalanderSettings})
            .then(response => {
                console.log(response.data)
            })
            .catch(error => {
                console.log(error)
            });
    }

    // Function to refresh calendar data (local + Google events)
    const refreshCalendarData = async () => {
        try {
            const token = await getAccessTokenSilently();
            
            // Fetch local events
            const calanderDataRes = await axios.get(`${process.env.REACT_APP_SERVER}calander/GetCalanderData`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            let allEvents = calanderDataRes.data;
            
            // Fetch Google events
            try {
                const startDate = new Date();
                startDate.setMonth(startDate.getMonth() - 3);
                const endDate = new Date();
                endDate.setMonth(endDate.getMonth() + 3);

                const googleEventsRes = await axios.get(`${process.env.REACT_APP_SERVER}google-calendar/events`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        startDate: startDate.toISOString(),
                        endDate: endDate.toISOString()
                    }
                });

                if (googleEventsRes.data.googleCalendarEnabled && googleEventsRes.data.events) {
                    const localGoogleEventIds = calanderDataRes.data
                        .filter(e => e.GoogleEventId)
                        .map(e => e.GoogleEventId);
                    
                    const uniqueGoogleEvents = googleEventsRes.data.events.filter(
                        ge => !localGoogleEventIds.includes(ge.GoogleEventId)
                    );
                    
                    console.log(`[Calendar Refresh] ${uniqueGoogleEvents.length} external Google events added`);
                    allEvents = [...allEvents, ...uniqueGoogleEvents];
                }
            } catch (googleError) {
                console.log('Could not fetch Google Calendar events:', googleError.message);
            }
            
            setCalanderData(allEvents);
            console.log(`[Calendar Refresh] Total events: ${allEvents.length}`);
        } catch (error) {
            console.error('Error refreshing calendar:', error);
        }
    };

    const syncToGoogleCalendar = async () => {
        try {
            setSyncing(true);
            setSyncMessage(null);
            const token = await getAccessTokenSilently();
            const response = await axios.post(`${process.env.REACT_APP_SERVER}google-calendar/sync-now`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                const { synced, deleted } = response.data;
                let message = '✅ ';
                if (synced > 0) message += `${synced} synced`;
                if (synced > 0 && deleted > 0) message += ', ';
                if (deleted > 0) message += `${deleted} cleaned up`;
                if (synced === 0 && deleted === 0) message += 'Already in sync!';
                setSyncMessage({ type: 'success', text: message });
                
                // Refresh calendar to show updated data (including new Google events)
                await refreshCalendarData();
            } else {
                setSyncMessage({ type: 'error', text: '❌ Sync failed. Please try again.' });
            }
            
            // Clear message after 5 seconds
            setTimeout(() => setSyncMessage(null), 5000);
        } catch (error) {
            console.error('Error syncing to Google Calendar:', error);
            setSyncMessage({ type: 'error', text: '❌ Failed to sync. Check Google Calendar connection.' });
            setTimeout(() => setSyncMessage(null), 5000);
        } finally {
            setSyncing(false);
        }
    }

    const initCalanderData = (settingsData) => {
        // First set the timezone to match user settings
        scheduleObj.current.timezone = settingsData.Timezone;

        // Configure the event settings to handle UTC dates from DB
        scheduleObj.current.eventSettings = {
            ...scheduleObj.current.eventSettings,
            timeZone: 'UTC',
            enableTooltip: settingsData.Tooltip === "On"
        };

        // Set other calendar settings
        scheduleObj.current.timeFormat = settingsData.TimeFormat;
        scheduleObj.current.timeScale.slotCount = settingsData.SlotInterval;
        scheduleObj.current.timeScale.interval = settingsData.SlotDuration;
        scheduleObj.current.workHours.end = settingsData.WorkEndHour;
        scheduleObj.current.workHours.start = settingsData.WorkStartHour;
        scheduleObj.current.endHour = settingsData.DayEndHour;
        scheduleObj.current.startHour = settingsData.DayStartHour;
        scheduleObj.current.firstDayOfWeek = settingsData.FirstDayOfTheWeek;
        scheduleObj.current.workDays = settingsData.WorkWeek;

        if (settingsData.WeekNumbers === "Off") {
            scheduleObj.current.showWeekNumber = false;
        } else {
            scheduleObj.current.showWeekNumber = true;
            scheduleObj.current.weekRule = settingsData.WeekNumbers;
        }
    };

    // Handler to hide the event editor popup for existing events
    const onPopupOpen = (args) => {
        // Block editing only for single (non-recurring) events
        if (
            args.type === 'Editor' &&
            args.data &&
            args.data.Id &&
            !args.data.RecurrenceRule // Only block if not a recurring event
        ) {
            args.cancel = true;
        }
    };

    return (<div className='schedule-control-section'>
        <div className='col-lg-12 control-section'>
            <div className='content-wrapper'>
                <div className='schedule-overview'>

                    <AppBarComponent colorMode="Primary">
                        <span className="time e-icons e-time-zone"></span>
                        <span id="timezoneBtn" className="time ">UTC</span>
                        <span className="time e-icons e-clock"></span>
                        <span id="timeBtn" className="time current-time" ref={timeBtn}></span>
                        <div className="e-appbar-spacer"></div>
                        {/* Sync to Google Calendar Button */}
                        {googleCalendarConnected && (
                            <div className='control-panel' style={{ marginRight: '10px' }}>
                                <ButtonComponent 
                                    id='syncGoogleBtn' 
                                    cssClass='e-inherit' 
                                    style={{ 
                                        backgroundColor: syncing ? 'rgba(100, 150, 200, 0.3)' : '#6366f1', 
                                        color: 'white', 
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        fontSize: '13px'
                                    }}
                                    onClick={syncToGoogleCalendar}
                                    disabled={syncing}
                                    content={syncing ? '⏳ Syncing...' : '🔄 Sync to Google'} 
                                />
                            </div>
                        )}
                        {/* Sync Message Toast */}
                        {syncMessage && (
                            <div style={{
                                position: 'fixed',
                                top: '80px',
                                right: '20px',
                                padding: '12px 20px',
                                backgroundColor: syncMessage.type === 'success' ? '#10b981' : '#ef4444',
                                color: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                                zIndex: 10000,
                                fontSize: '14px',
                                border: '1px solid rgba(100, 150, 200, 0.15)'
                            }}>
                                {syncMessage.text}
                            </div>
                        )}
                        {/* <div className='control-panel calendar-export'>
                            <ButtonComponent id='printBtn' cssClass='title-bar-btn e-inherit' iconCss='e-icons e-print' onClick={(onPrint)} content='Print' />
                        </div>
                        <div className='control-panel import-button'>
                            <UploaderComponent id='fileUpload' type='file' allowedExtensions='.ics' cssClass='calendar-import' buttons={{ browse: importTemplateFn({ text: 'Import' })[0] }} multiple={false} showFileList={false} selected={(onImportClick)} created={createUpload} />
                        </div>
                        <div className='control-panel calendar-export'>
                            <DropDownButtonComponent id='exportBtn' content='Export' cssClass='e-inherit' items={calanderSettings.ExportItems} select={onExportClick} />
                        </div> */}
                        <ButtonComponent id='settingsBtn' cssClass='overview-toolbar-settings e-inherit' iconCss='e-icons e-settings' iconPosition='Top' content='' onClick={btnClick} />
                    </AppBarComponent>

                    <ToolbarComponent id='toolbarOptions' cssClass='overview-toolbar' width='100%' height={70} overflowMode='Scrollable' scrollStep={100} created={() => liveTimeInterval = setInterval(() => { updateLiveTime(); }, 1000)} clicked={onToolbarItemClicked}>
                        <ItemsDirective>
                            <ItemDirective prefixIcon='e-icons e-plus' tooltipText='New Event' text='New Event' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-repeat' tooltipText='New Recurring Event' text='New Recurring Event' tabIndex={0} />
                            <ItemDirective type='Separator' />
                            <ItemDirective prefixIcon='e-icons e-day' tooltipText='Day' text='Day' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-week' tooltipText='Week' text='Week' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-week' tooltipText='WorkWeek' text='WorkWeek' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-month' tooltipText='Month' text='Month' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-month' tooltipText='Year' text='Year' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-agenda-date-range' tooltipText='Agenda' text='Agenda' tabIndex={0} />
                            <ItemDirective tooltipText='Timeline Views' text='Timeline Views' template={timelineTemplate} />
                            <ItemDirective type='Separator' />
                            <ItemDirective tooltipText='Grouping' text='Grouping' template={groupTemplate} />
                            <ItemDirective tooltipText='Timme Slots' text='Timme Slots' template={gridlineTemplate} />
                            <ItemDirective tooltipText='Auto Fit Rows' text='Auto Fit Rows' template={autoHeightTemplate} />
                        </ItemsDirective>
                    </ToolbarComponent>

                    <div className='overview-content'>

                        <div className='left-panel'>
                            <div className='overview-scheduler'>
                                <ScheduleComponent id='scheduler' cssClass='schedule-overview'
                                    ref={scheduleObj} width='100%'
                                    height='100%' currentView={currentView}
                                    group={{ resources: ['Calendars'] }}
                                    timezone='UTC'
                                    eventSettings={{ dataSource: calanderData }}
                                    actionComplete={onActionComplete}
                                    dateHeaderTemplate={dateHeaderTemplate}
                                    popupOpen={onPopupOpen}>
                                    <ResourcesDirective>
                                        <ResourceDirective field='CalendarId' title='Calendars' name='Calendars' dataSource={calanderSettings.CalendarCollections} query={new Query().where('CalendarId', 'equal', 1)} textField='CalendarText' idField='CalendarId' colorField='CalendarColor' />
                                    </ResourcesDirective>
                                    <ViewsDirective>
                                        <ViewDirective option='Day' />
                                        <ViewDirective option='Week' />
                                        <ViewDirective option='WorkWeek' />
                                        <ViewDirective option='Month' />
                                        <ViewDirective option='Year' />
                                        <ViewDirective option='Agenda' />
                                        <ViewDirective option='TimelineDay' />
                                        <ViewDirective option='TimelineWeek' />
                                        <ViewDirective option='TimelineWorkWeek' />
                                        <ViewDirective option='TimelineMonth' />
                                        <ViewDirective option='TimelineYear' />
                                    </ViewsDirective>
                                    <Inject services={[Day, Week, WorkWeek, Month, Year, Agenda, TimelineViews, TimelineMonth, TimelineYear, DragAndDrop, Resize, Print, ExcelExport, ICalendarImport, ICalendarExport]} />
                                </ScheduleComponent>
                                <ContextMenuComponent id='overviewContextMenu' cssClass='schedule-context-menu' ref={contextMenuObj} target='.e-schedule' items={calanderSettings.ContextMenuItems} beforeOpen={contextMenuOpen} select={contextMenuSelect} />
                            </div>
                        </div>

                        <div className='right-panel hide'>
                            <div className='control-panel e-css'>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Calendar</label>
                                    </div>
                                    <div className='col-right'>
                                        <MultiSelectComponent id="resources" cssClass='schedule-resource' ref={resourceObj} dataSource={calanderSettings.CalendarCollections} mode='CheckBox' fields={{ text: 'CalendarText', value: 'CalendarId' }} enableSelectionOrder={false} showClearButton={false} showDropDownIcon={true} popupHeight={300} value={[1]} change={onResourceChange}>
                                            <Inject services={[CheckBoxSelection]} />
                                        </MultiSelectComponent>
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>First Day of Week</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="weekFirstDay" dataSource={calanderSettings.WeekDays} fields={{ text: 'text', value: 'value' }} value={userCalanderSettings.FirstDayOfTheWeek} popupHeight={400} change={onWorkDayChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Work week</label>
                                    </div>
                                    <div className='col-right'>
                                        <MultiSelectComponent id="workWeekDays" cssClass='schedule-workweek' ref={workWeekObj} dataSource={calanderSettings.WeekDays} mode='CheckBox' fields={{ text: 'text', value: 'value' }} enableSelectionOrder={false} showClearButton={false} showDropDownIcon={true} value={userCalanderSettings.WorkWeek} change={onWorkWeekChange}>
                                            <Inject services={[CheckBoxSelection]} />
                                        </MultiSelectComponent>
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Timezone</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="timezone" dataSource={calanderSettings.TimezoneData} fields={{ text: 'text', value: 'value' }} value={userCalanderSettings.Timezone} popupHeight={150} change={timezoneChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Day Start Hour</label>
                                    </div>
                                    <div className='col-right'>
                                        <TimePickerComponent id='dayStartHour' showClearButton={false} value={userCalanderSettings.DayStartHour} change={onDayStartHourChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Day End Hour</label>
                                    </div>
                                    <div className='col-right'>
                                        <TimePickerComponent id='dayEndHour' showClearButton={false} value={userCalanderSettings.DayEndHour} change={onDayEndHourChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Work Start Hour</label>
                                    </div>
                                    <div className='col-right'>
                                        <TimePickerComponent id='workHourStart' showClearButton={false} value={userCalanderSettings.WorkStartHour} change={onWorkStartChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Work End Hour</label>
                                    </div>
                                    <div className='col-right'>
                                        <TimePickerComponent id='workHourEnd' showClearButton={false} value={userCalanderSettings.WorkEndHour} change={onWorkEndChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Slot Duration</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="slotDuration" dataSource={calanderSettings.MajorSlotData} fields={{ text: 'Name', value: 'Value' }} value={userCalanderSettings.SlotDuration} popupHeight={150} change={onTimeSlotDurationChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Slot Interval</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="slotInterval" dataSource={calanderSettings.MinorSlotData} value={userCalanderSettings.SlotInterval} popupHeight={150} change={onTimeSlotIntervalChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Time Format</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="timeFormat" dataSource={calanderSettings.TimeFormatData} fields={{ text: 'Name', value: 'Value' }} value={userCalanderSettings.TimeFormat} popupHeight={150} change={onTimeFormatChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Week Numbers</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="weekNumber" dataSource={calanderSettings.WeekNumberData} fields={{ text: 'Name', value: 'Value' }} value={userCalanderSettings.WeekNumbers} popupHeight={150} change={weekNumberChange} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Tooltip</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="tooltip" dataSource={calanderSettings.TooltipData} fields={{ text: 'Name', value: 'Value' }} value={userCalanderSettings.Tooltip} popupHeight={150} change={tooltipChange} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        {/* Calendar Assistant */}
        <RightToolbar 
            isPanelPinned={isPanelPinned}
            isAssistantOpen={isAssistantOpen}
            onToggleAssistant={onToggleAssistant}
            hasNewMessages={hasNewMessages}
        />
        <CalendarAssistantPanel />
    </div>);
};

// Main component wrapped with CalendarAssistantProvider
const Calander = () => {
    return (
        <CalendarAssistantProvider>
            <CalanderContent />
        </CalendarAssistantProvider>
    );
};

export default Calander;